import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Globe, Loader2, ArrowRight, Facebook, Instagram, Youtube, ShieldCheck } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api, imgUrl } from "@/lib/api";

const SOCIALS = [
  ["facebook", Facebook, "Facebook"],
  ["instagram", Instagram, "Instagram"],
  ["youtube", Youtube, "YouTube"],
];

function normUrl(v, base) {
  if (!v) return null;
  if (v.startsWith("http")) return v;
  if (v.startsWith("@")) return base ? base + v.slice(1) : null;
  if (base) return base + v;
  return "https://" + v;
}

export default function FirmCard() {
  const { slug } = useParams();
  const [firm, setFirm] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    setState("loading");
    api.get(`/firm/card/${slug}`)
      .then((r) => { setFirm(r.data); setState("ok"); })
      .catch(() => setState("notfound"));
  }, [slug]);

  if (state === "loading") {
    return <div className="min-h-screen bg-ink flex items-center justify-center"><Loader2 className="w-8 h-8 text-gold animate-spin" /></div>;
  }
  if (state === "notfound") {
    return (
      <div className="bg-ink min-h-screen text-white">
        <Header />
        <div className="max-w-2xl mx-auto px-5 py-32 text-center">
          <h1 className="font-display text-4xl mb-3">Vizitka sa nenašla</h1>
          <p className="text-white/50 mb-6">Táto firemná vizitka neexistuje alebo ešte nebola schválená.</p>
          <Link to="/" className="text-gold inline-flex items-center gap-1 hover:underline">Späť na hlavnú stránku <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <Footer />
      </div>
    );
  }

  const refs = (firm.references || []).filter((r) => r.image || r.image_after);
  const socialLinks = [
    ...SOCIALS.map(([k, Icon, label]) => [normUrl(firm[k], `https://${label.toLowerCase()}.com/`), Icon, label]),
    [normUrl(firm.tiktok, "https://tiktok.com/@"), null, "TikTok"],
    [normUrl(firm.x, "https://x.com/"), null, "X"],
  ].filter(([u]) => u);

  return (
    <div className="bg-ink min-h-screen text-white" data-testid="firm-card-page">
      <Header />
      <section className="max-w-4xl mx-auto px-5 md:px-8 pt-28 md:pt-32 pb-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col md:flex-row md:items-center gap-6 border-b border-white/10 pb-8">
          {firm.logo ? (
            <img src={imgUrl(firm.logo)} alt={firm.company} className="w-24 h-24 object-contain bg-white/5 border border-white/10 p-2" data-testid="firm-card-logo" />
          ) : (
            <div className="w-24 h-24 bg-gold/10 border border-gold/20 flex items-center justify-center"><ShieldCheck className="w-10 h-10 text-gold" /></div>
          )}
          <div className="flex-1">
            <div className="flex items-center gap-2 text-gold text-xs font-mono2 uppercase tracking-[0.25em] mb-2"><MapPin className="w-3.5 h-3.5" /> Okres {firm.district}{firm.city ? ` · ${firm.city}` : ""}</div>
            <h1 className="font-display text-4xl md:text-5xl" data-testid="firm-card-name">{firm.company}</h1>
            <div className="flex flex-wrap gap-4 mt-4 text-sm">
              {firm.phone && <a href={`tel:${firm.phone}`} className="flex items-center gap-1.5 text-white/80 hover:text-gold" data-testid="firm-card-phone"><Phone className="w-4 h-4 text-gold" />{firm.phone}</a>}
              {firm.email && <a href={`mailto:${firm.email}`} className="flex items-center gap-1.5 text-white/80 hover:text-gold"><Mail className="w-4 h-4 text-gold" />{firm.email}</a>}
              {firm.website && <a href={normUrl(firm.website)} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-white/80 hover:text-gold"><Globe className="w-4 h-4 text-gold" />Web</a>}
            </div>
          </div>
        </motion.div>

        {firm.bio && <p className="text-white/70 text-lg leading-relaxed mt-8 max-w-2xl" data-testid="firm-card-bio">{firm.bio}</p>}

        {socialLinks.length > 0 && (
          <div className="flex flex-wrap gap-3 mt-6" data-testid="firm-card-socials">
            {socialLinks.map(([u, Icon, label], i) => (
              <a key={i} href={u} target="_blank" rel="noreferrer" className="flex items-center gap-2 border border-white/15 hover:border-gold/60 px-4 py-2 text-sm text-white/70 hover:text-gold transition-colors">
                {Icon ? <Icon className="w-4 h-4" /> : null} {label}
              </a>
            ))}
          </div>
        )}

        {refs.length > 0 && (
          <div className="mt-14" data-testid="firm-card-refs">
            <h2 className="font-display text-3xl mb-6">Referencie</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {refs.map((r, i) => (
                <div key={i} className="border border-white/10 bg-ink-2 overflow-hidden">
                  <div className="grid grid-cols-2">
                    {r.image && <div className="relative"><img src={imgUrl(r.image)} alt="pred" className="w-full h-44 object-cover" /><span className="absolute top-2 left-2 bg-black/60 text-white text-[10px] uppercase px-2 py-0.5 tracking-widest">Pred</span></div>}
                    {r.image_after && <div className="relative"><img src={imgUrl(r.image_after)} alt="po" className="w-full h-44 object-cover" /><span className="absolute top-2 left-2 bg-gold text-ink text-[10px] uppercase px-2 py-0.5 tracking-widest">Po</span></div>}
                  </div>
                  {r.description && <div className="p-4 text-white/60 text-sm">{r.description}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-16 border border-gold/30 bg-gold/5 p-8 text-center" data-testid="firm-card-cta">
          <h3 className="font-display text-2xl md:text-3xl mb-2">Chcete cenovú ponuku na zateplenie?</h3>
          <p className="text-white/60 mb-5">Vypočítajte si orientačnú cenu online — odošlite dopyt a firmy vo vašom okrese sa vám ozvú.</p>
          <Link to="/" className="inline-flex items-center gap-2 bg-gold text-ink px-7 py-3.5 font-bold uppercase tracking-wider hover:bg-gold-light transition-colors">
            Spustiť kalkulačku <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
